import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { UseSearchPageStateReturn } from '@klareospace/pharos-lib-front-utils';
import type {
  CustomerStatus,
  CustomerType,
  CustomerWithResolvedRefs,
  SortDirection,
} from '@klareospace/pharos-lib-types';
import { CUSTOMER_STATUS, CUSTOMER_TYPE } from '@klareospace/pharos-lib-types';
import { usePersistedPageSize } from '@klareospace/pharos-lib-web-utils';

import { useMasterContext } from '&/master';
import type { SearchCustomersParams } from '../api';
import type { CustomerSortableColumn } from '../types/customer.types';
import { useCustomers } from './use-customers';

// ─── Constants ────────────────────────────────────────────────────────────────

const CUSTOMERS_PAGE_SIZE_STORAGE_KEY = 'customers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseCustomersPageReturn {
  currentPage: number;
  customers: CustomerWithResolvedRefs[];
  error: Error | null;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onScopeFilterChange: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: CustomerSortableColumn, sortDirection: SortDirection | null) => void;
  onStatusFilterChange: (values: string[]) => void;
  onTypeFilterChange: (values: string[]) => void;
  pageSize: number;
  pageSizeOptions: number[];
  scopeFilter: string[];
  searchValue: string;
  sortBy: CustomerSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  statusFilter: CustomerStatus[];
  totalItems: number;
  totalPages: number;
  typeFilter: CustomerType[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VALID_STATUSES = new Set<string>(Object.values(CUSTOMER_STATUS));
const VALID_TYPES = new Set<string>(Object.values(CUSTOMER_TYPE));

function toValidStatuses(values: string[]): CustomerStatus[] {
  return values.filter((v): v is CustomerStatus => VALID_STATUSES.has(v));
}

function toValidTypes(values: string[]): CustomerType[] {
  return values.filter((v): v is CustomerType => VALID_TYPES.has(v));
}

function toParams(
  searchState: UseSearchPageStateReturn<CustomerSortableColumn>,
  scopeFilter: string[],
  statusFilter: CustomerStatus[],
  typeFilter: CustomerType[],
  siteId: string | null,
  spaceId: string | null,
): SearchCustomersParams {
  // Scope filter from the action bar takes precedence; otherwise derive from master context
  const effectiveSiteIds = scopeFilter.length > 0 ? scopeFilter : undefined;
  return {
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch || undefined,
    siteId: effectiveSiteIds ? undefined : (siteId ?? undefined),
    siteIds: effectiveSiteIds,
    sort: searchState.sort,
    // Always include the parent spaceId so the backend can surface space-level and
    // tenant-level customers when a site (or multi-site action bar filter) is active.
    spaceId: spaceId ?? undefined,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
    types: typeFilter.length > 0 ? typeFilter : undefined,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCustomersPage(): UseCustomersPageReturn {
  const { siteId, spaceId } = useMasterContext();
  const { options: pageSizeOptions, pageSize, setPageSize } = usePersistedPageSize(CUSTOMERS_PAGE_SIZE_STORAGE_KEY);
  const searchState = useSearchPageState<CustomerSortableColumn>({ pageSize });
  const [scopeFilter, setScopeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<CustomerStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<CustomerType[]>([]);

  const params = toParams(searchState, scopeFilter, statusFilter, typeFilter, siteId, spaceId);

  const { data, error, isLoading } = useCustomers(params);

  function onScopeFilterChange(values: string[]): void {
    setScopeFilter(values);
    searchState.onPageChange(1);
  }

  function onStatusFilterChange(values: string[]): void {
    setStatusFilter(toValidStatuses(values));
    searchState.onPageChange(1);
  }

  function onTypeFilterChange(values: string[]): void {
    setTypeFilter(toValidTypes(values));
    searchState.onPageChange(1);
  }

  return {
    currentPage: searchState.currentPage,
    customers: data?.items ?? [],
    error: error ?? null,
    isLoading,
    onPageChange: searchState.onPageChange,
    onPageSizeChange: setPageSize,
    onScopeFilterChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onStatusFilterChange,
    onTypeFilterChange,
    pageSize: searchState.pageSize,
    pageSizeOptions,
    scopeFilter,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    statusFilter,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    typeFilter,
  };
}
